class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.detailsContainer = this.querySelector('details > *:last-child');
    this.summaryToggle = this.querySelector('summary');
    this.closeButton = this.querySelector('button[type="button"]');

    this.detailsContainer?.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'visibility' && !this.mainDetailsToggle.hasAttribute('open')) {
          this.detailsContainer.removeAttribute('style');
        }
      }
    );

    this.summaryToggle?.addEventListener('click', this.onSummaryClick.bind(this));
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    this.summaryToggle?.setAttribute('role', 'button');

    this.mainDetailsToggle?.addEventListener('toggle', this.onDetailsToggle.bind(this));
  }

  lockPageScroll() {
    // overflow:hidden on html/body would break position:sticky elsewhere on
    // the page (the PDP image column), so block the scroll INPUTS instead:
    // no layout change, nothing to restore, sticky untouched. Scrolling
    // INSIDE the results panel still works.
    if (this._scrollLocked) return;
    this._scrollLocked = true;
    this._blockScroll = (event) => {
      if (event.target instanceof Element && event.target.closest('#cloud-search-results')) return;
      event.preventDefault();
    };
    window.addEventListener('wheel', this._blockScroll, { passive: false });
    window.addEventListener('touchmove', this._blockScroll, { passive: false });
  }

  unlockPageScroll() {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;
    window.removeEventListener('wheel', this._blockScroll);
    window.removeEventListener('touchmove', this._blockScroll);
  }

  isMobileSearchViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  // Mobile search is a separate full-screen UI, never this modal: XCloud's
  // takeover when the app runs the search, BA Search's drawer
  // (window.BASearchMobile) otherwise. The modal opens on mobile only as a
  // fallback if BA Search is enabled but its script hasn't loaded.
  usesExternalMobileSearch() {
    if (!this.isMobileSearchViewport()) return false;
    // Post-XCloud: BA's drawer is the only external mobile search. If it
    // is not there (script failed, disabled), the theme modal must serve -
    // the old !BASearchConfig branch pointed at XCloud, which no longer
    // exists, and left taps doing nothing.
    return !!window.BASearchMobile;
  }

  onDetailsToggle() {
    if (this.usesExternalMobileSearch()) {
      // THE mobile search entry point. The magnifier in the mobile header is
      // this <summary>; .header__search-bar is display:none at every width,
      // so nothing bound to it is ever in a real tap path.
      //
      // The theme <details> must not open on mobile - the drawer replaces it -
      // but something has to. XCloud used to listen on this same summary and
      // raise its takeover, so closing the details here was enough. When the
      // app was uninstalled that listener went with it and this branch was
      // left closing the details and returning, which is precisely "tapping
      // search does nothing" for every mobile visitor.
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.mainDetailsToggle.removeAttribute('open');
        // Removing the attribute queues another toggle; that pass sees no
        // 'open' and falls straight through, so this cannot recurse.
        this.triggerCloudSearchMobile();
      }
      return;
    }

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.lockPageScroll();
      const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
      if (searchModal) searchModal.classList.add('search-modal--open');
      // Opens that bypass openModal() (the native <details> toggle from the
      // keyboard, or a click that landed before this script upgraded the
      // element) still put the caret in the field.
      const input = this.getSearchInput();
      if (input && document.activeElement !== input) input.focus({ preventScroll: true });
      this.scheduleCloudSearchReposition();
    } else {
      this.unlockPageScroll();
      this.mainDetailsToggle.querySelector('.search-modal')?.classList.remove('search-modal--open');
      this.hideCloudSearchResults();
    }
  }

  getSearchInput() {
    return this.querySelector('.search__input, #Search-In-Modal');
  }

  hideCloudSearchResults() {
    const input = this.getSearchInput();
    if (input) input.value = '';

    // With BA Search, ba-search.js is the sole owner of the dropdown.
    if (window.BASearchConfig) return;
    document.getElementById('cloud-search-results')?.setAttribute('hidden', '');
  }

  triggerCloudSearchMobile() {
    const input = this.getSearchInput();
    if (!input) return;

    this.mainDetailsToggle.removeAttribute('open');
    if (window.BASearchMobile) {
      // A tap must NEVER die silently: if the drawer throws, the theme
      // modal is the fallback. (The old fallback, input.click(), was
      // XCloud's takeover trigger - dead since the app was uninstalled.)
      try {
        window.BASearchMobile.open(input);
        return;
      } catch (e) {
        // fall through to the native modal
      }
    }
    this.openModal();
  }

  repositionCloudSearchResults() {
    // BA Search positions its own dropdown; repositioning from here (and the
    // synthetic window resize it dispatches) must never run alongside it.
    if (window.BASearchConfig) return;
    if (this.isMobileSearchViewport()) return;

    const results = document.getElementById('cloud-search-results');
    const input = this.getSearchInput();
    if (!results || !input) return;

    const rect = input.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    results.removeAttribute('hidden');
    results.style.removeProperty('display');
    results.classList.remove('cloud-search-mobile');

    const left = Math.round(rect.left);
    const top = Math.round(rect.bottom + 16);

    results.style.position = 'absolute';
    results.style.margin = '0';
    results.style.inset = `${top}px auto auto ${left}px`;
    results.style.transform = 'none';
    results.style.removeProperty('left');
    results.style.removeProperty('top');
    results.style.width = '400px';
    results.style.maxWidth = `calc(100vw - ${left}px - 16px)`;
    results.style.maxHeight = 'none';
    results.style.overflow = 'visible';
    results.style.zIndex = '10001';

    window.dispatchEvent(new Event('resize'));
  }

  ensureCloudSearchResultsObserver() {
    if (window.BASearchConfig) return;
    if (this._cloudSearchResultsObserver) return;

    const results = document.getElementById('cloud-search-results');
    if (!results) return;

    this._cloudSearchResultsObserver = new MutationObserver(() => {
      if (!this.isOpen() || this.isMobileSearchViewport()) return;

      const results = document.getElementById('cloud-search-results');
      const input = this.getSearchInput();
      if (!results || !input) return;

      const rect = input.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      const expectedTop = Math.round(rect.bottom + 16);
      const inset = results.style.inset || '';
      const match = inset.match(/^([\d.]+)px/);
      const currentTop = match ? Math.round(parseFloat(match[1])) : null;

      if (currentTop === expectedTop) return;

      this.repositionCloudSearchResults();
    });

    this._cloudSearchResultsObserver.observe(results, {
      attributes: true,
      attributeFilter: ['style', 'hidden'],
    });
  }

  scheduleCloudSearchReposition() {
    if (window.BASearchConfig) return;
    this.ensureCloudSearchResultsObserver();
    [0, 50, 200, 450].forEach((delay) => {
      setTimeout(() => this.repositionCloudSearchResults(), delay);
    });
  }

  isOpen() {
    return this.mainDetailsToggle.hasAttribute('open');
  }

  onSummaryClick(event) {
    if (this.usesExternalMobileSearch()) {
      event.preventDefault();
      this.triggerCloudSearchMobile();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isOpen() ? this.closeModal(event, true) : this.openModal();
  }

  onBodyClick(event) {
    const isOverlayClick = event.target.classList.contains('modal-overlay');
    const isCloseButtonClick = event.target.closest('.search-modal__close-button');
    const isSearchContent = event.target.closest('.search-modal__content');

    if ((isOverlayClick && !isSearchContent) || isCloseButtonClick) {
      event.preventDefault();
      event.stopPropagation();

      this.hideCloudSearchResults();
      this.close(false);
    }
  }

  openModal() {
    if (this.usesExternalMobileSearch()) return;

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);

    this.lockPageScroll();

    this.mainDetailsToggle.setAttribute('open', '');

    const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
    requestAnimationFrame(() => {
      if (searchModal) searchModal.classList.add('search-modal--open');
    });

    const firstFocusable = this.mainDetailsToggle.querySelector(
      'input:not([type="hidden"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus({ preventScroll: true });
    }

    this.scheduleCloudSearchReposition();

    const input = this.getSearchInput();
    if (input && !this._cloudSearchRepositionBound) {
      this._cloudSearchRepositionBound = true;
      input.addEventListener('focus', () => this.scheduleCloudSearchReposition());
    }
  }

  closeModal(event, animating = true) {
    if (this.usesExternalMobileSearch()) return;

    if (event) event.preventDefault();
    document.body.removeEventListener('click', this.onBodyClickEvent);

    const searchModal = this.mainDetailsToggle.querySelector('.search-modal');
    if (searchModal) {
      searchModal.classList.remove('search-modal--open');
      // The no-JS fallback pins opacity to 1 while details[open] remains (the
      // attribute only clears in the timeout below), which held the modal
      // fully visible through the whole close and made it snap shut at the
      // end. --closing outranks that rule so the fade actually plays.
      searchModal.classList.add('search-modal--closing');
    }

    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute('open');
      searchModal?.classList.remove('search-modal--closing');
      this.unlockPageScroll();
      this.hideCloudSearchResults();

      if (animating) {
        this.detailsContainer.style.setProperty('visibility', 'visible');
      }
    }, 300);
  }

  close(animating = true) {
    this.closeModal(null, animating);
  }
}

customElements.define('details-modal', DetailsModal);
